import { useState } from "react";
import { captureException } from "scripts/logging/error-report";
import { fetchWrapper } from "services/api-client/fetchWrapper";
import { useParams } from "react-router-dom";
import { trackEvent, matomoUserId } from "services/api-client/matomoTracker";
import { useUserContext } from "services/UserContext";
import { buildCopyMarkdown } from "./ChatArtifacts";
import { RiThumbUpFill, RiThumbDownFill, RiThumbUpLine, RiThumbDownLine, RiClipboardLine } from "react-icons/ri";
import { useQueryFlags } from "scripts/hooks/useQueryFlags";

const ratingsMap: Record<number, string> = {
    1: "good",
    [-1]: "bad",
};

interface MessageActionBarProps {
    messageId?: number;
    initialRating?: number;
    content?: string;
    metadata?: any;
    className?: string;
}

export function MessageActionBar({
    messageId = 0,
    initialRating = 0,
    content = '',
    metadata = {},
    className = '',
}: MessageActionBarProps) {
    const { session, harid } = useParams();
    const { user } = useUserContext();
    const { isContentNotVisible } = useQueryFlags();
    const [rating, setRating] = useState<string>(() => ratingsMap[initialRating ?? 0]);
    const [isCopied, setIsCopied] = useState(false);
    const [isLike, setIsLike] = useState(false);
    const [isDislike, setIsDislike] = useState(false);
    const [previousRating, setPreviousRating] = useState(rating || "");

    const handlelike = async (response: string) => {
        if (!session || !messageId) return;
        if (response === previousRating) {
            response = "";
        }
        if (response === "good") {
            setIsLike(!isLike);
            setIsDislike(false);
        } else if (response === "bad") {
            setIsDislike(!isDislike);
            setIsLike(false);
        } else {
            setIsDislike(false);
            setIsLike(false);
        }
        try {
            await fetchWrapper.post("/api/v1/chatbot/rating", {
                assistant: harid ? "property" : "search",
                session_id: session,
                rating: response,
                message_id: messageId,
            });
            setPreviousRating(response);
            setRating(response);
            if (response === "good" || response === "bad") {
                trackEvent(`AI Mode / Message ${response === "good" ? "Like" : "Dislike"}`, { uid: matomoUserId(user) });
            }
        } catch (e) {
            console.error(e);
            captureException(e);
        }
    };

    // 🧰 legacy fallback
    const execCommandCopy = (text: string) => {
        try {
            const ta = document.createElement("textarea");
            ta.value = text;
            ta.style.position = "fixed";
            ta.style.opacity = "0";
            ta.style.left = "-9999px";
            document.body.appendChild(ta);
            ta.focus();
            ta.select();
            const ok = document.execCommand("copy");
            document.body.removeChild(ta);
            return ok;
        } catch (error) {
            captureException(error);
            console.error(`execCommandCopy failed: ${error}`);
            return false;
        }
    };

    const handleCopyText = async () => {
        try {
            const text = buildCopyMarkdown(content.replaceAll(/(?<!\n)\n</g, "\n\n<"), metadata) || '';

            if (!text) {
                console.warn("No text to copy");
                return;
            }

            if (navigator.clipboard && window.isSecureContext) {
                try {
                    await navigator.clipboard.writeText(text);
                    setIsCopied(true);
                    trackEvent("AI Mode / Message Copy", { uid: matomoUserId(user) });
                    setTimeout(() => setIsCopied(false), 2000);
                    return;
                } catch (clipboardError: any) {
                    if (clipboardError.name === 'NotAllowedError' ||
                        clipboardError.message?.includes('permissions policy')) {
                        console.warn("Clipboard API blocked by permissions policy, using fallback");
                    } else {
                        console.warn("Clipboard API failed:", clipboardError);
                    }
                    captureException(clipboardError);
                }
            }

            const ok = execCommandCopy(text);
            if (ok) {
                setIsCopied(true);
                trackEvent("AI Mode / Message Copy", { uid: matomoUserId(user) });
                setTimeout(() => setIsCopied(false), 2000);
            } else {
                console.error("All copy methods failed. Text to copy:", text.substring(0, 100));
            }
        } catch (e) {
            console.error("Copy failed:", e);
            captureException(e);
        }
    };

    return (
        <div className={`ai_bubble_footer pt-2 ${className}`} style={{ marginLeft: 35 }}>
            {isContentNotVisible ? null : (
                <div className="ai_bubble__icons">
                    <div className="d-flex align-items-center">
                        <button
                            onClick={() => handlelike("good")}
                            className="btn_icon p-1 p-sm-2"
                            title="I like this"
                        >
                            {(rating === "good" || isLike) ? (
                                <RiThumbUpFill size={17} color="#FD4B78" />
                            ) : (
                                <RiThumbUpLine size={17} color="#5d5e5e" />
                            )}
                        </button>
                        <button
                            onClick={() => handlelike("bad")}
                            className="btn_icon p-1 p-sm-2"
                            title="I dislike this"
                        >
                            {(rating === "bad" || isDislike) ? (
                                <RiThumbDownFill size={17} color="#FD4B78" />
                            ) : (
                                <RiThumbDownLine size={17} color="#5d5e5e" />
                            )}
                        </button>
                        <button
                            className={`btn_icon ${isCopied ? "p-2 d-flex align-items-center gap-quarter" : "p-1 p-sm-2"}`}
                            onClick={handleCopyText}
                            aria-label="Copy message to clipboard"
                            title="Copy message to clipboard"
                        >
                            {isCopied ? (
                                <>
                                    <img
                                        className="mb-1"
                                        src="https://content.harstatic.com/media/icons/emails/checkmark-green.png"
                                        alt="Checkmark"
                                    />
                                    <span className="btn_icon_text_green p-0"> COPIED </span>
                                </>
                            ) : (
                                <RiClipboardLine size={17} color="#5d5e5e" />
                            )}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
